import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  AddressFieldDefinition,
  AddressValue,
  ChoiceFieldDefinition,
  DropdownFieldDefinition,
  FormFieldDefinition,
  FileUploadFieldDefinition,
} from "@rwaforms/shared";
import { evaluateConditionalLogic, computeCalculatedOutputs, INDIAN_STATES } from "@rwaforms/shared";
import {
  resolveShare,
  consumeShare,
  fetchFormVersionByShareToken,
  fetchFormDetail,
  submitFormResponse,
} from "../lib/api-client";
import { FileUploadField } from "../components/FileUploadField";
import { useAuth } from "../contexts/AuthContext";

/**
 * Safe wrapper for useAuth that returns null values if auth fails
 * This allows SharePage to work even when authentication is unavailable
 */
function useAuthSafe(): { isAuthenticated: boolean; user: ReturnType<typeof useAuth>["user"] } {
  try {
    const auth = useAuth();
    return {
      isAuthenticated: auth.isAuthenticated,
      user: auth.user,
    };
  } catch (error) {
    // Auth context unavailable or failed - log warning but don't break the page
    console.warn("Authentication unavailable for share page:", error);
    return {
      isAuthenticated: false,
      user: null,
    };
  }
}

function SharePage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const _navigate = useNavigate();
  const { isAuthenticated, user } = useAuthSafe();
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // First, resolve the share to check if password is required
  const resolveQuery = useQuery({
    queryKey: ["share-resolve", token],
    queryFn: () => resolveShare(token!),
    enabled: Boolean(token),
    retry: false,
  });

  // Consume the share (with password if needed)
  const consumeMutation = useMutation({
    mutationFn: (pwd?: string | null) => consumeShare(token!, pwd),
    onSuccess: () => {
      // Refetch resolve to get updated share info
      void resolveQuery.refetch();
    },
    onError: (error: Error & { status?: number }) => {
      if (error.status === 401) {
        setPasswordError("Invalid password. Please try again.");
      } else {
        setPasswordError(error.message || "Failed to access share.");
      }
    },
  });

  // Fetch form version via share token (public endpoint, no auth required)
  // The backend handles both cases: specific version ID or published version
  const formVersionQuery = useQuery({
    queryKey: ["form-version-share", token],
    queryFn: () => {
      if (!token) {
        throw new Error("Share token not available");
      }
      // Use the public share endpoint instead of authenticated endpoint
      return fetchFormVersionByShareToken(token);
    },
    enabled: Boolean(
      token &&
      resolveQuery.isSuccess &&
      resolveQuery.data?.share &&
      (consumeMutation.isSuccess || resolveQuery.data?.share.shareType === "public")
    ),
  });

  // Fallback: only fetch form detail if user is authenticated (for edit link)
  // This is optional and should not be required for the form to work
  const formDetailQuery = useQuery({
    queryKey: ["form-detail", resolveQuery.data?.share.formId],
    queryFn: () => fetchFormDetail(resolveQuery.data!.share.formId),
    enabled: Boolean(
      isAuthenticated &&
      resolveQuery.data?.share.formId &&
      (consumeMutation.isSuccess || resolveQuery.data?.share.shareType === "public")
    ),
    // Don't retry or fail the page if this fails - it's only for the edit link
    retry: false,
  });

  const schema =
    formVersionQuery.data?.schema ??
    formDetailQuery.data?.publishedVersion?.schema ??
    formDetailQuery.data?.draft?.schema;

  const formTitle =
    (formVersionQuery.data as { formTitle?: string })?.formTitle ??
    formDetailQuery.data?.form.title ??
    formVersionQuery.data?.schema?.title ??
    "Shared Form";

  const formDescription =
    (formVersionQuery.data as { formDescription?: string })?.formDescription ??
    formDetailQuery.data?.form.description ??
    null;

  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fileMetadata, setFileMetadata] = useState<Record<string, Array<{
    fileName: string;
    contentType: string;
    sizeBytes: number;
    tempKey: string;
  }>>>({});
  const [activeUploads, setActiveUploads] = useState(0);
  const formStartTimeRef = useRef<number | null>(null);
  const submissionAttemptedRef = useRef(false);
  const prefillAppliedRef = useRef(false);

  // Track form start time when form becomes visible
  useEffect(() => {
    if (schema && !formStartTimeRef.current) {
      formStartTimeRef.current = Date.now();
    }
  }, [schema]);

  // Pre-fill form fields from URL parameters
  // Supports:
  // - prefill_<fieldId>=value (by field ID)
  // - prefill_<fieldTitle>=value (by field title, case-insensitive)
  // - _prefill_email, _prefill_name, _prefill_phone (special keywords)
  useEffect(() => {
    if (!schema || prefillAppliedRef.current) return;

    const searchParams = new URLSearchParams(window.location.search);
    const prefillData: Record<string, unknown> = {};

    // Build lookup maps for fields
    const fieldById = new Map<string, { id: string; type: string }>();
    const fieldByTitle = new Map<string, { id: string; type: string }>();
    const fieldByKeyword = new Map<string, { id: string; type: string }>();

    for (const field of schema.fields) {
      fieldById.set(field.id, { id: field.id, type: field.type });
      fieldByTitle.set(field.title.toLowerCase(), { id: field.id, type: field.type });

      // Map special keywords to field types
      if (field.type === "email") {
        fieldByKeyword.set("email", { id: field.id, type: field.type });
      } else if (field.type === "phone") {
        fieldByKeyword.set("phone", { id: field.id, type: field.type });
      } else if (field.type === "name") {
        fieldByKeyword.set("name", { id: field.id, type: field.type });
      }
    }

    searchParams.forEach((value, key) => {
      let targetField: { id: string; type: string } | undefined;

      // Check for prefill_ prefix
      if (key.startsWith("prefill_")) {
        const identifier = key.slice(8); // Remove "prefill_"

        // Try to match by field ID first
        targetField = fieldById.get(identifier);

        // If not found, try by title (case-insensitive)
        if (!targetField) {
          targetField = fieldByTitle.get(identifier.toLowerCase());
        }
      }

      // Check for special _prefill_ keywords
      if (key.startsWith("_prefill_")) {
        const keyword = key.slice(9); // Remove "_prefill_"
        targetField = fieldByKeyword.get(keyword.toLowerCase());
      }

      if (targetField) {
        // Parse value based on field type
        if (targetField.type === "number") {
          const numValue = Number(value);
          if (!isNaN(numValue)) {
            prefillData[targetField.id] = numValue;
          }
        } else if (targetField.type === "checkboxes" || targetField.type === "dropdown") {
          // Support comma-separated values for multi-select
          prefillData[targetField.id] = value.split(",").map(v => v.trim());
        } else {
          prefillData[targetField.id] = value;
        }
      }
    });

    if (Object.keys(prefillData).length > 0) {
      setAnswers(current => ({ ...current, ...prefillData }));
      prefillAppliedRef.current = true;
    }
  }, [schema]);

  const logic = useMemo(() => {
    if (!schema) return null;
    return evaluateConditionalLogic(schema, answers);
  }, [schema, answers]);

  const calculated = useMemo(() => {
    if (!schema) return {};
    return computeCalculatedOutputs(schema, answers);
  }, [schema, answers]);

  // Form submission mutation
  const submitMutation = useMutation({
    mutationFn: async (data: {
      formId: string;
      formVersionId: string;
      shareToken?: string;
      responseData: Record<string, unknown>;
      files?: Array<{
        fieldId: string;
        fileName: string;
        contentType: string;
        sizeBytes: number;
        tempKey: string;
      }>;
      duration: number;
    }) => {
      return submitFormResponse(data.formId, {
        shareToken: data.shareToken,
        formVersionId: data.formVersionId,
        responseData: data.responseData,
        files: data.files,
        metadata: {
          duration: data.duration,
        },
      });
    },
    onSuccess: () => {
      setIsSubmitted(true);
      submissionAttemptedRef.current = true;
    },
    onError: (error: Error & { status?: number }) => {
      submissionAttemptedRef.current = true;
      console.error("Form submission error:", error);
    },
  });

  // Validate form before submission
  const validateForm = (): { valid: boolean; errors: Record<string, string> } => {
    if (!schema) return { valid: false, errors: {} };

    const errors: Record<string, string> = {};

    schema.fields.forEach((field) => {
      // Skip hidden fields
      if (hiddenFields.has(field.id)) {
        return;
      }

      // Skip calculated fields (they're computed)
      if (field.type === "calculated") {
        return;
      }

      const value = answers[field.id];

      // Helper to get error message (use custom if provided)
      const getErrorMessage = (defaultMessage: string) =>
        field.validationMessage || defaultMessage;

      // Check required fields
      if (field.required) {
        if (value === undefined || value === null || value === "") {
          errors[field.id] = getErrorMessage("This field is required");
          return;
        }

        // For arrays (checkboxes), check if empty
        if (Array.isArray(value) && value.length === 0) {
          errors[field.id] = getErrorMessage("This field is required");
          return;
        }
      }

      // Type-specific validation
      if (field.type === "number" && value !== undefined && value !== null && value !== "") {
        const numValue = typeof value === "number" ? value : Number(value);
        if (isNaN(numValue)) {
          errors[field.id] = getErrorMessage("Please enter a valid number");
        } else {
          if (field.validation?.min !== undefined && numValue < field.validation.min) {
            errors[field.id] = getErrorMessage(`Value must be at least ${field.validation.min}`);
          }
          if (field.validation?.max !== undefined && numValue > field.validation.max) {
            errors[field.id] = getErrorMessage(`Value must be at most ${field.validation.max}`);
          }
        }
      }

      if (field.type === "short_text" || field.type === "long_text") {
        const textValue = String(value || "");
        if (field.validation?.minLength && textValue.length < field.validation.minLength) {
          errors[field.id] = getErrorMessage(`Text must be at least ${field.validation.minLength} characters`);
        }
        if (field.validation?.maxLength && textValue.length > field.validation.maxLength) {
          errors[field.id] = getErrorMessage(`Text must be at most ${field.validation.maxLength} characters`);
        }
      }

      if (field.type === "email" && value && String(value).trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(String(value))) {
          errors[field.id] = getErrorMessage("Please enter a valid email address");
        }
      }

      if (field.type === "phone" && value && String(value).trim() !== "") {
        // Basic phone validation: allows +, (), -, space, and digits. At least 7 digits.
        const validChars = /^[0-9+\-\(\)\s.]+$/;
        const digits = String(value).replace(/[^0-9]/g, "").length;

        if (!validChars.test(String(value)) || digits < 7) {
          errors[field.id] = getErrorMessage("Please enter a valid phone number");
        }
      }

      // Name field validation - only allow Hindi and English characters
      if (field.type === "name" && value && String(value).trim() !== "") {
        const nameValue = String(value).trim();

        // Unicode ranges:
        // \u0900-\u097F: Devanagari script (Hindi)
        // A-Za-z: English letters
        // Also allow spaces, hyphens, apostrophes, periods (common in names)
        const hindiEnglishNameRegex = /^[\u0900-\u097FA-Za-z\s\-'.]+$/;

        // Check if allowHindiEnglishOnly is explicitly set to false
        const validation = (field as { validation?: { allowHindiEnglishOnly?: boolean; pattern?: string; patternDescription?: string; minLength?: number; maxLength?: number } }).validation;
        const shouldValidateCharacters = validation?.allowHindiEnglishOnly !== false;

        if (shouldValidateCharacters && !hindiEnglishNameRegex.test(nameValue)) {
          errors[field.id] = getErrorMessage("Name should only contain Hindi or English characters");
        }

        // Check minLength if specified
        if (validation?.minLength && nameValue.length < validation.minLength) {
          errors[field.id] = getErrorMessage(`Name must be at least ${validation.minLength} characters`);
        }

        // Check maxLength if specified
        if (validation?.maxLength && nameValue.length > validation.maxLength) {
          errors[field.id] = getErrorMessage(`Name must be at most ${validation.maxLength} characters`);
        }

        // Check custom pattern if specified
        if (validation?.pattern) {
          try {
            const customRegex = new RegExp(validation.pattern);
            if (!customRegex.test(nameValue)) {
              errors[field.id] = getErrorMessage(validation.patternDescription || "Name format is invalid");
            }
          } catch {
            // Invalid regex pattern - skip custom validation
          }
        }
      }

      // Validate dropdown selection
      if (field.type === "dropdown" && field.required && (!value || value === "")) {
        errors[field.id] = getErrorMessage("Please select an option");
      }

      // Validate address field
      if (field.type === "address") {
        const addressField = field as AddressFieldDefinition;
        const addressValue = (value as AddressValue) || {};
        const isFieldRequired = field.required;
        const requiredFields = addressField.requiredFields || {};

        // Validation patterns
        const englishAlphanumericPattern = /^[A-Za-z0-9\s\-\/,.']+$/;
        const englishOnlyPattern = /^[A-Za-z\s\-']+$/;
        const pincodePattern = /^[0-9]{6}$/;

        // House Number validation - English + Numeric
        if (isFieldRequired && requiredFields.houseNumber !== false && !addressValue.houseNumber?.trim()) {
          errors[field.id] = getErrorMessage("House number is required");
        } else if (addressValue.houseNumber?.trim() && !englishAlphanumericPattern.test(addressValue.houseNumber.trim())) {
          errors[field.id] = getErrorMessage("House number can only contain English letters, numbers, and basic symbols (- / , . ')");
        }

        // Village validation - English only
        if (!errors[field.id] && isFieldRequired && requiredFields.village !== false && !addressValue.village?.trim()) {
          errors[field.id] = getErrorMessage("Village is required");
        } else if (!errors[field.id] && addressValue.village?.trim() && !englishOnlyPattern.test(addressValue.village.trim())) {
          errors[field.id] = getErrorMessage("Village name should only contain English letters");
        }

        // Post validation - English only
        if (!errors[field.id] && isFieldRequired && requiredFields.post !== false && !addressValue.post?.trim()) {
          errors[field.id] = getErrorMessage("Post is required");
        } else if (!errors[field.id] && addressValue.post?.trim() && !englishOnlyPattern.test(addressValue.post.trim())) {
          errors[field.id] = getErrorMessage("Post name should only contain English letters");
        }

        // District validation - English only
        if (!errors[field.id] && isFieldRequired && requiredFields.district !== false && !addressValue.district?.trim()) {
          errors[field.id] = getErrorMessage("District is required");
        } else if (!errors[field.id] && addressValue.district?.trim() && !englishOnlyPattern.test(addressValue.district.trim())) {
          errors[field.id] = getErrorMessage("District name should only contain English letters");
        }

        // State validation - must be valid Indian state
        if (!errors[field.id] && isFieldRequired && requiredFields.state !== false && !addressValue.state) {
          errors[field.id] = getErrorMessage("State is required");
        } else if (!errors[field.id] && addressValue.state && !INDIAN_STATES.includes(addressValue.state as typeof INDIAN_STATES[number])) {
          errors[field.id] = getErrorMessage("Please select a valid state");
        }

        // Pincode validation - exactly 6 digits
        if (!errors[field.id] && isFieldRequired && requiredFields.pincode !== false && !addressValue.pincode?.trim()) {
          errors[field.id] = getErrorMessage("Pincode is required");
        } else if (!errors[field.id] && addressValue.pincode?.trim() && !pincodePattern.test(addressValue.pincode.trim())) {
          errors[field.id] = getErrorMessage("Pincode must be exactly 6 digits");
        }
      }
    });

    return { valid: Object.keys(errors).length === 0, errors };
  };

  // Update document title
  useEffect(() => {
    // Fetch app title from meta tag to avoid hardcoding
    const appTitle = document.querySelector('meta[name="application-name"]')?.getAttribute('content') || "Rojgar with Ankit Forms";

    if (schema?.title) {
      document.title = `${schema.title} - ${appTitle}`;
    } else {
      document.title = appTitle;
    }

    // Reset title when unmounting
    return () => {
      document.title = appTitle;
    };
  }, [schema?.title]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!schema || !share) return;

    // Block submission if uploads are active
    if (activeUploads > 0) {
      alert("Please wait for all files to finish uploading before submitting.");
      return;
    }

    // Validate form
    const validation = validateForm();
    setFieldErrors(validation.errors);

    if (!validation.valid) {
      // Scroll to first error
      const firstErrorField = Object.keys(validation.errors)[0];
      if (firstErrorField) {
        const element = document.getElementById(`field-${firstErrorField}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    // Calculate duration
    const duration = formStartTimeRef.current
      ? Math.round((Date.now() - formStartTimeRef.current) / 1000)
      : 0;

    // Prepare response data (exclude calculated fields from submission)
    const responseData: Record<string, unknown> = {};
    schema.fields.forEach((field) => {
      if (field.type !== "calculated" && !hiddenFields.has(field.id)) {
        const value = answers[field.id];
        if (value !== undefined && value !== null && value !== "") {
          responseData[field.id] = value;
        }
      }
    });

    // Extract file uploads from fileMetadata
    const files: Array<{
      fieldId: string;
      fileName: string;
      contentType: string;
      sizeBytes: number;
      tempKey: string;
    }> = [];

    schema.fields.forEach((field) => {
      if (field.type === "file_upload" && !hiddenFields.has(field.id)) {
        const metadata = fileMetadata[field.id];
        if (metadata && metadata.length > 0) {
          metadata.forEach((fileMeta) => {
            files.push({
              fieldId: field.id,
              fileName: fileMeta.fileName,
              contentType: fileMeta.contentType,
              sizeBytes: fileMeta.sizeBytes,
              tempKey: fileMeta.tempKey,
            });
          });
        }
      }
    });

    // Determine form version ID
    const formVersionId =
      resolveQuery.data?.share.formVersionId ||
      formVersionQuery.data?.id ||
      formDetailQuery.data?.publishedVersion?.id ||
      formDetailQuery.data?.draft?.versionId ||
      "";

    if (!formVersionId) {
      setFieldErrors({ _form: "Unable to determine form version. Please refresh the page." });
      return;
    }

    // Submit form
    await submitMutation.mutateAsync({
      formId: share.formId,
      formVersionId,
      shareToken: token || undefined,
      responseData,
      files: files.length > 0 ? files : undefined,
      duration,
    });
  };

  // Handle loading state
  if (!token) {
    return (
      <div className="share-page-container">
        <div className="share-page-content">
          <h1>Invalid Share Link</h1>
          <p>The share link is missing a token.</p>
        </div>
      </div>
    );
  }

  // Handle error states
  if (resolveQuery.isError) {
    const error = resolveQuery.error as Error & { status?: number };
    if (error.status === 404) {
      return (
        <div className="share-page-container">
          <div className="share-page-content">
            <h1>Share Not Found</h1>
            <p>This share link is invalid or has been deleted.</p>
          </div>
        </div>
      );
    }
    if (error.status === 410) {
      return (
        <div className="share-page-container">
          <div className="share-page-content">
            <h1>Share Expired</h1>
            <p>This share link has expired or reached its usage limit.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="share-page-container">
        <div className="share-page-content">
          <h1>Error Loading Share</h1>
          <p>{error.message || "An error occurred while loading the share."}</p>
        </div>
      </div>
    );
  }

  // Show loading while resolving
  if (resolveQuery.isLoading) {
    return (
      <div className="share-page-container">
        <div className="share-page-content">
          <FormSkeleton />
        </div>
      </div>
    );
  }

  const share = resolveQuery.data?.share;
  if (!share) {
    return (
      <div className="share-page-container">
        <div className="share-page-content">
          <h1>Share Not Found</h1>
        </div>
      </div>
    );
  }

  // Check if share needs to be consumed first
  // For one-time shares, we need to consume before showing the form
  // For password-protected shares, we consume after password verification
  // For public shares, we consume on first view to track usage (but allow form to load)
  const needsConsumption = share.shareType === "one_time" || share.shareType === "public";
  const isConsumed = consumeMutation.isSuccess;

  // Auto-consume one-time and public shares immediately
  if (needsConsumption && !isConsumed && !consumeMutation.isPending && !consumeMutation.isError) {
    void consumeMutation.mutate(undefined);
  }

  // Show password form if password is required and not yet consumed
  if (share.requiresPassword && !isConsumed) {
    return (
      <div className="share-page-container">
        <div className="share-page-content" style={{ maxWidth: "400px" }}>
          <div className="surface" style={{ padding: "2rem", display: "grid", gap: "1rem" }}>
            <h1 style={{ margin: 0 }}>Password Required</h1>
            <p style={{ color: "var(--color-text-muted)" }}>
              This shared form is password-protected. Please enter the password to continue.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setPasswordError(null);
                consumeMutation.mutate(password || null);
              }}
            >
              <div style={{ display: "grid", gap: "0.75rem" }}>
                <div>
                  <label htmlFor="password" style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500 }}>
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError(null);
                    }}
                    style={{
                      width: "100%",
                      borderRadius: "10px",
                      border: passwordError
                        ? "1px solid var(--color-error)"
                        : "1px solid var(--color-border)",
                      padding: "0.6rem 0.75rem",
                      fontSize: "0.95rem",
                    }}
                    autoFocus
                  />
                  {passwordError && (
                    <p style={{ margin: "0.5rem 0 0", color: "var(--color-error)", fontSize: "0.85rem" }}>
                      {passwordError}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  className="button primary"
                  disabled={consumeMutation.isPending || !password}
                  style={{ width: "100%" }}
                >
                  {consumeMutation.isPending ? "Verifying..." : "Continue"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while consuming (for non-public shares) or fetching form
  // For public shares, allow form to load even if consumption is pending
  const isPublicShare = share.shareType === "public";
  const shouldWaitForConsumption = !isPublicShare && needsConsumption && !isConsumed;

  // Show loading if:
  // 1. We need to wait for consumption (non-public shares that need consumption)
  // 2. Consumption is pending (for non-public shares)
  // 3. Form queries are loading and we have the required data
  if (
    shouldWaitForConsumption ||
    (!isPublicShare && consumeMutation.isPending) ||
    ((isConsumed || isPublicShare) && !schema && !formVersionQuery.isError && !formDetailQuery.isError && (formVersionQuery.isLoading || formDetailQuery.isLoading))
  ) {
    return (
      <div className="share-page-container">
        <div className="share-page-content">
          <FormSkeleton />
        </div>
      </div>
    );
  }

  // Show error if form fetch failed
  // For public shares, check errors even if not consumed yet
  if ((isConsumed || isPublicShare) && formVersionQuery.isError && formDetailQuery.isError) {
    return (
      <div className="share-page-container">
        <div className="share-page-content">
          <h1>Error Loading Form</h1>
          <p>Unable to load the form. Please try again later.</p>
        </div>
      </div>
    );
  }

  // Render the form
  if (!schema) {
    return (
      <div className="share-page-container">
        <div className="share-page-content">
          <p>Form schema not available.</p>
        </div>
      </div>
    );
  }

  const hiddenFields = logic?.hiddenFieldIds ?? new Set<string>();

  // Check if authenticated user has access to edit this form
  // Only show edit link if auth is available and user has access
  // If auth fails or formDetailQuery fails, gracefully skip the edit link
  const canEditForm = isAuthenticated &&
    user &&
    formDetailQuery.data?.form?.teamId &&
    user.teamIds.includes(formDetailQuery.data.form.teamId);
  const editLink = canEditForm
    ? `/teams/${formDetailQuery.data?.form.teamId}/forms/${share.formId}`
    : null;

  // Show success message after submission
  if (isSubmitted && submitMutation.isSuccess) {
    return (
      <div className="share-page-container">
        <div className="share-page-content">
          <div className="surface" style={{ padding: "4rem 2rem", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "1.5rem" }}>
            <div style={{
              width: "80px",
              height: "80px",
              background: "var(--color-success-bg, rgba(16, 185, 129, 0.1))",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-success, #10b981)",
              fontSize: "40px",
              marginBottom: "0.5rem"
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>

            <div style={{ maxWidth: "500px" }}>
              <h1 style={{ margin: "0 0 0.5rem", fontSize: "2rem", fontWeight: 700, color: "var(--color-text)" }}>
                Thank You!
              </h1>
              <p style={{ margin: "0", color: "var(--color-text-muted)", fontSize: "1.1rem", lineHeight: 1.6 }}>
                Your response has been submitted successfully.
              </p>
            </div>

            {share.shareType === "public" && (
              <button
                onClick={() => window.location.reload()}
                className="button secondary"
                style={{ marginTop: "1rem" }}
              >
                Submit another response
              </button>
            )}

            <div style={{ marginTop: "2rem", paddingTop: "2rem", borderTop: "1px solid var(--color-border)", width: "100%", maxWidth: "300px" }}>
              <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", margin: 0 }}>
                Powered by <strong style={{ color: "var(--color-text)" }}>{document.querySelector('meta[name="application-name"]')?.getAttribute('content') || "Rojgar with Ankit Forms"}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="share-page-container">
      <div className="share-page-content">
        {editLink && (
          <div style={{
            marginBottom: "1rem",
            textAlign: "right",
            fontSize: "0.875rem"
          }}>
            <Link
              to={editLink}
              style={{
                color: "var(--color-text-muted)",
                textDecoration: "none"
              }}
            >
              Edit in Builder →
            </Link>
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          {/* Form Header - Title & Description (Google Forms style) */}
          <div className="surface" style={{
            padding: "0",
            marginBottom: "1rem",
            borderRadius: "8px",
            overflow: "hidden"
          }}>
            <div style={{
              height: "10px",
              background: "var(--color-primary, #4f46e5)",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px"
            }} />
            <div style={{ padding: "1.5rem 2rem" }}>
              <h1 style={{
                margin: 0,
                fontSize: "2rem",
                fontWeight: 600,
                color: "var(--color-text)"
              }}>
                {formTitle}
              </h1>
              {formDescription && (
                <p style={{
                  margin: "0.75rem 0 0",
                  color: "var(--color-text-muted)",
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap"
                }}>
                  {formDescription}
                </p>
              )}
            </div>
          </div>

          <div className="surface" style={{ padding: "2rem", display: "grid", gap: "1.5rem" }}>
            {fieldErrors._form && (
              <div style={{
                padding: "0.75rem 1rem",
                background: "var(--color-error-bg, #fee)",
                border: "1px solid var(--color-error, #c33)",
                borderRadius: "8px",
                color: "var(--color-error, #c33)",
                fontSize: "0.9rem"
              }}>
                {fieldErrors._form}
              </div>
            )}

            {/* Render fields grouped by sections */}
            {(() => {
              // Group fields by section
              const sections = schema.sections || [];
              const sortedSections = [...sections].sort((a, b) => a.position - b.position);

              // Get root-level fields (no section)
              const rootFields = schema.fields
                .filter((f) => !f.sectionId)
                .sort((a, b) => a.position - b.position);

              // Build section groups
              const sectionGroups = sortedSections.map((section) => ({
                section,
                fields: schema.fields
                  .filter((f) => f.sectionId === section.id)
                  .sort((a, b) => a.position - b.position),
              }));

              const renderField = (field: typeof schema.fields[0]) => {
                if (hiddenFields.has(field.id)) return null;
                const value =
                  field.type === "calculated"
                    ? calculated[field.id] ?? ""
                    : (answers[field.id] as string | number | boolean | string[] | undefined);
                return (
                  <div key={field.id} id={`field-${field.id}`}>
                    <FieldRenderer
                      formId={share.formId}
                      field={field}
                      value={value}
                      error={fieldErrors[field.id]}
                      fileMetadata={field.type === "file_upload" ? fileMetadata[field.id] : undefined}
                      shareToken={token}
                      onChange={(newValue) => {
                        setAnswers((current) => ({
                          ...current,
                          [field.id]: newValue,
                        }));
                        if (fieldErrors[field.id]) {
                          setFieldErrors((current) => {
                            const next = { ...current };
                            delete next[field.id];
                            return next;
                          });
                        }
                      }}
                      onFileMetadataChange={field.type === "file_upload" ? (metadata) => {
                        setFileMetadata((current) => ({
                          ...current,
                          [field.id]: metadata,
                        }));
                      } : undefined}
                      onUploadStatusChange={field.type === "file_upload" ? (isUploading) => {
                        setActiveUploads((prev) => isUploading ? prev + 1 : Math.max(0, prev - 1));
                      } : undefined}
                    />
                  </div>
                );
              };

              return (
                <>
                  {/* Root fields (no section) */}
                  {rootFields.length > 0 && (
                    <div style={{ display: "grid", gap: "1.5rem" }}>
                      {rootFields.map(renderField)}
                    </div>
                  )}

                  {/* Sections with their fields */}
                  {sectionGroups.map(({ section, fields }) => {
                    const visibleFields = fields.filter((f) => !hiddenFields.has(f.id));
                    if (visibleFields.length === 0) return null;

                    const isTwoColumn = section.layout === "two_column";

                    return (
                      <div key={section.id} style={{ marginTop: "1rem" }}>
                        {/* Section header */}
                        {(section.title || section.description) && (
                          <div style={{ marginBottom: "1rem" }}>
                            {section.title && (
                              <h3 style={{
                                fontSize: "1.125rem",
                                fontWeight: 600,
                                marginBottom: "0.25rem",
                                color: "var(--color-text)"
                              }}>
                                {section.title}
                              </h3>
                            )}
                            {section.description && (
                              <p style={{
                                margin: 0,
                                fontSize: "0.9rem",
                                color: "var(--color-text-muted)"
                              }}>
                                {section.description}
                              </p>
                            )}
                          </div>
                        )}
                        {/* Section fields - apply two-column grid if layout is set */}
                        <div style={{
                          display: "grid",
                          gap: "1.5rem",
                          gridTemplateColumns: isTwoColumn ? "repeat(2, 1fr)" : "1fr",
                        }}>
                          {visibleFields.map(renderField)}
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}

            <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <button
                type="submit"
                className="button primary"
                disabled={submitMutation.isPending || isSubmitted}
                style={{
                  minWidth: "120px",
                  padding: "0.75rem 1.5rem",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {submitMutation.isPending ? "Submitting..." : isSubmitted ? "Submitted" : "Submit"}
              </button>
            </div>

            {submitMutation.isError && (
              <div style={{
                padding: "0.75rem 1rem",
                background: "var(--color-error-bg, #fee)",
                border: "1px solid var(--color-error, #c33)",
                borderRadius: "8px",
                color: "var(--color-error, #c33)",
                fontSize: "0.9rem"
              }}>
                {submitMutation.error instanceof Error
                  ? submitMutation.error.message
                  : "An error occurred while submitting your response. Please try again."}
              </div>
            )}
          </div>
        </form>
      </div>
      <style>{`
        .share-page-container {
          min-height: 100vh;
          background: var(--color-bg, #f5f5f5);
          padding: 2rem 1rem;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }
        
        .share-page-content {
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
        }
        
        @media (max-width: 768px) {
          .share-page-container {
            padding: 1rem 0.75rem;
          }
          
          .share-page-content {
            max-width: 100%;
          }
          
          .share-page-content h1 {
            font-size: 1.75rem !important;
          }
        }
      `}</style>
    </div>
  );
}

function FieldRenderer({
  formId,
  field,
  value,
  error,
  fileMetadata,
  onChange,
  onFileMetadataChange,
  onUploadStatusChange,
  shareToken,
}: {
  formId: string;
  field: FormFieldDefinition;
  value: unknown;
  error?: string;
  fileMetadata?: Array<{
    fileName: string;
    contentType: string;
    sizeBytes: number;
    tempKey: string;
  }>;
  onChange(value: unknown): void;
  onFileMetadataChange?: (metadata: Array<{
    fileName: string;
    contentType: string;
    sizeBytes: number;
    tempKey: string;
  }>) => void;
  onUploadStatusChange?: (isUploading: boolean) => void;
  shareToken?: string;
}): JSX.Element {

  const label = (
    <div style={{ marginBottom: "0.5rem" }}>
      <p style={{ margin: 0, fontWeight: 600 }}>
        {field.title || "Untitled field"}
        {field.required && <span style={{ color: "var(--color-error, #c33)", marginLeft: "0.25rem" }}>*</span>}
      </p>
      {field.description ? (
        <p style={{ margin: "0.2rem 0 0", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
          {field.description}
        </p>
      ) : null}
    </div>
  );

  const inputStyle: React.CSSProperties = {
    ...INPUT_STYLE,
    ...(error ? {
      borderColor: "var(--color-error, #c33)",
      borderWidth: "2px",
    } : {}),
  };

  switch (field.type) {
    case "short_text":
    case "long_text":
      return (
        <div>
          {label}
          <textarea
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            rows={field.type === "long_text" ? 4 : 2}
            style={inputStyle}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? `error-${field.id}` : undefined}
          />
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    case "number":
      return (
        <div>
          {label}
          <input
            type="number"
            value={value === undefined ? "" : String(value)}
            onChange={(event) => onChange(event.target.value ? Number(event.target.value) : "")}
            style={inputStyle}
            min={field.validation?.min}
            max={field.validation?.max}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? `error-${field.id}` : undefined}
          />
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    case "email":
      return (
        <div>
          {label}
          <input
            type="email"
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            style={inputStyle}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? `error-${field.id}` : undefined}
            placeholder="email@example.com"
            autoComplete="email"
          />
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    case "phone":
      return (
        <div>
          {label}
          <input
            type="tel"
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            style={inputStyle}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? `error-${field.id}` : undefined}
            placeholder="+91 (555) 123-4567"
            autoComplete="tel"
          />
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    case "name":
      return (
        <div>
          {label}
          <input
            type="text"
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            style={inputStyle}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? `error-${field.id}` : undefined}
            placeholder="Your name"
            autoComplete="name"
          />
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    case "multiple_choice": {
      const choiceField = field as ChoiceFieldDefinition;
      return (
        <div>
          {label}
          <div style={{ display: "grid", gap: "0.35rem" }}>
            {choiceField.options?.map((option) => (
              <label key={option.id} style={{ display: "flex", gap: "0.6rem" }}>
                <input
                  type="radio"
                  name={field.id}
                  checked={value === option.id}
                  onChange={() => onChange(option.id)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      );
    }
    case "checkboxes": {
      const choiceField = field as ChoiceFieldDefinition;
      const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
      return (
        <div>
          {label}
          <div style={{ display: "grid", gap: "0.35rem" }}>
            {choiceField.options?.map((option) => (
              <label key={option.id} style={{ display: "flex", gap: "0.6rem" }}>
                <input
                  type="checkbox"
                  checked={selected.has(option.id)}
                  onChange={(event) => {
                    const next = new Set(selected);
                    if (event.target.checked) {
                      next.add(option.id);
                    } else {
                      next.delete(option.id);
                    }
                    onChange(Array.from(next));
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      );
    }
    case "dropdown": {
      const dropdown = field as DropdownFieldDefinition;
      return (
        <div>
          {label}
          <select
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            style={inputStyle}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? `error-${field.id}` : undefined}
          >
            <option value="">Select…</option>
            {dropdown.options?.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    }
    case "file_upload": {
      const uploadField = field as FileUploadFieldDefinition;
      // FileUploadField stores keys, but we need metadata for submission
      // We'll need to track metadata separately since FileUploadField only returns keys
      // For now, we'll use the FileUploadField as-is and handle metadata extraction
      // in a wrapper or modify FileUploadField to accept metadata callback
      return (
        <FileUploadFieldWrapper
          formId={formId}
          field={uploadField}
          value={value as string | string[] | undefined}
          fileMetadata={fileMetadata}
          onChange={(newValue) => onChange(newValue)}
          onFileMetadataChange={onFileMetadataChange}
          onUploadStatusChange={onUploadStatusChange}
          error={error}
          shareToken={shareToken}
        />
      );
    }
    case "calculated":
      return (
        <div>
          {label}
          <div style={{ padding: "0.6rem 0.75rem", background: "var(--color-bg)", borderRadius: "10px" }}>
            {String(value ?? "0")}
          </div>
        </div>
      );
    case "rating": {
      const ratingField = field as FormFieldDefinition & {
        style?: "stars" | "numbers" | "emojis";
        scale?: number;
        lowLabel?: string;
        highLabel?: string;
      };
      const scale = ratingField.scale ?? 5;
      const style = ratingField.style ?? "stars";

      return (
        <div>
          {label}
          <div style={{ display: "grid", gap: "0.35rem" }}>
            {Array.from({ length: scale }, (_, i) => i + 1).map((num) => (
              <label key={num} style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                <input
                  type="radio"
                  name={field.id}
                  value={num}
                  checked={Number(value) === num}
                  onChange={() => onChange(num)}
                />
                {style === "stars" && "⭐".repeat(num)}
                {style === "numbers" && num}
                {style === "emojis" && ["😞", "😐", "😊", "😄", "😍"][num - 1] || num}
              </label>
            ))}
            {(ratingField.lowLabel || ratingField.highLabel) && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                <span>{ratingField.lowLabel}</span>
                <span>{ratingField.highLabel}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    case "date":
      return (
        <div>
          {label}
          <input
            type="date"
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            style={inputStyle}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? `error-${field.id}` : undefined}
          />
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    case "time":
      return (
        <div>
          {label}
          <input
            type="time"
            value={String(value ?? "")}
            onChange={(event) => onChange(event.target.value)}
            style={inputStyle}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={error ? `error-${field.id}` : undefined}
          />
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    case "static_text": {
      const staticField = field as FormFieldDefinition & { content?: string; format?: string };
      return (
        <div style={{ padding: "0.5rem 0" }}>
          <p style={{ margin: 0, lineHeight: 1.6, color: "var(--color-text)" }}>
            {staticField.content || ""}
          </p>
        </div>
      );
    }
    case "address": {
      const addressField = field as AddressFieldDefinition;
      const addressValue = (value as AddressValue) || {};
      const labels = addressField.labels || {};
      const placeholders = addressField.placeholders || {};
      const defaultCountry = addressField.defaultCountry || "India";

      const updateAddressField = (subField: keyof AddressValue, subValue: string) => {
        onChange({
          ...addressValue,
          [subField]: subValue,
        });
      };

      const subFieldStyle: React.CSSProperties = {
        ...inputStyle,
        marginBottom: "0.75rem",
      };

      return (
        <div>
          {label}
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {/* House Number */}
            <div>
              <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                {labels.houseNumber || "House Number"}
                {field.required && <span style={{ color: "var(--color-error, #c33)", marginLeft: "0.25rem" }}>*</span>}
              </label>
              <input
                type="text"
                value={addressValue.houseNumber || ""}
                onChange={(e) => updateAddressField("houseNumber", e.target.value)}
                placeholder={placeholders.houseNumber || "Enter house/flat number"}
                style={subFieldStyle}
              />
            </div>

            {/* Two-column layout for Village and Post */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              {/* Village */}
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.village || "Village"}
                  {field.required && <span style={{ color: "var(--color-error, #c33)", marginLeft: "0.25rem" }}>*</span>}
                </label>
                <input
                  type="text"
                  value={addressValue.village || ""}
                  onChange={(e) => updateAddressField("village", e.target.value)}
                  placeholder={placeholders.village || "Enter village name"}
                  style={subFieldStyle}
                />
              </div>

              {/* Post */}
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.post || "Post"}
                  {field.required && <span style={{ color: "var(--color-error, #c33)", marginLeft: "0.25rem" }}>*</span>}
                </label>
                <input
                  type="text"
                  value={addressValue.post || ""}
                  onChange={(e) => updateAddressField("post", e.target.value)}
                  placeholder={placeholders.post || "Enter post office name"}
                  style={subFieldStyle}
                />
              </div>
            </div>

            {/* Two-column layout for District and State */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              {/* District */}
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.district || "District"}
                  {field.required && <span style={{ color: "var(--color-error, #c33)", marginLeft: "0.25rem" }}>*</span>}
                </label>
                <input
                  type="text"
                  value={addressValue.district || ""}
                  onChange={(e) => updateAddressField("district", e.target.value)}
                  placeholder={placeholders.district || "Enter district name"}
                  style={subFieldStyle}
                />
              </div>

              {/* State - Dropdown with Indian states */}
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.state || "State"}
                  {field.required && <span style={{ color: "var(--color-error, #c33)", marginLeft: "0.25rem" }}>*</span>}
                </label>
                <select
                  value={addressValue.state || ""}
                  onChange={(e) => updateAddressField("state", e.target.value)}
                  style={subFieldStyle}
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Two-column layout for Country and Pincode */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              {/* Country - Default India, readonly */}
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.country || "Country"}
                </label>
                <input
                  type="text"
                  value={addressValue.country || defaultCountry}
                  readOnly
                  style={{ ...subFieldStyle, backgroundColor: "var(--color-surface-elevated, var(--color-bg))", opacity: 0.7, cursor: "not-allowed" }}
                />
              </div>

              {/* Pincode - 6 digits only */}
              <div>
                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.85rem", fontWeight: 500 }}>
                  {labels.pincode || "Pincode"}
                  {field.required && <span style={{ color: "var(--color-error, #c33)", marginLeft: "0.25rem" }}>*</span>}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={addressValue.pincode || ""}
                  onChange={(e) => {
                    // Only allow numeric input
                    const numericValue = e.target.value.replace(/[^0-9]/g, "");
                    updateAddressField("pincode", numericValue);
                  }}
                  placeholder={placeholders.pincode || "Enter 6-digit pincode"}
                  style={subFieldStyle}
                />
              </div>
            </div>
          </div>
          {error && (
            <p id={`error-${field.id}`} style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
              {error}
            </p>
          )}
        </div>
      );
    }
    default:
      return (
        <div>
          {label}
          <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
            Field type not yet supported in share view.
          </div>
        </div>
      );
  }
}

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  borderRadius: "10px",
  border: "1px solid var(--color-border)",
  padding: "0.6rem 0.75rem",
  fontSize: "0.95rem",
  resize: "vertical",
};

/**
 * Wrapper for FileUploadField that tracks file metadata for submission
 */
function FileUploadFieldWrapper({
  formId,
  field,
  value,
  fileMetadata: _fileMetadata,
  onChange,
  onFileMetadataChange,
  onUploadStatusChange,
  error,
  shareToken,
}: {
  formId: string;
  field: FileUploadFieldDefinition;
  value: string | string[] | undefined;
  fileMetadata?: Array<{
    fileName: string;
    contentType: string;
    sizeBytes: number;
    tempKey: string;
  }>;
  onChange: (value: string | string[]) => void;
  onFileMetadataChange?: (metadata: Array<{
    fileName: string;
    contentType: string;
    sizeBytes: number;
    tempKey: string;
  }>) => void;
  onUploadStatusChange?: (isUploading: boolean) => void;
  error?: string;
  shareToken?: string;
}): JSX.Element {
  // Note: FileUploadField currently only stores keys, not metadata
  // For a complete implementation, FileUploadField would need to be enhanced
  // to accept and call a metadata callback, or store metadata alongside keys
  // For now, this is a placeholder that works with the current implementation
  // File metadata would need to be extracted from the upload session or stored separately

  return (
    <div>
      <FileUploadField
        formId={formId}
        field={field}
        value={value}
        onChange={onChange}
        shareToken={shareToken}
        onUploadStatusChange={onUploadStatusChange}
        onFileMetadataChange={onFileMetadataChange}
      />
      {error && (
        <p style={{ margin: "0.5rem 0 0", color: "var(--color-error, #c33)", fontSize: "0.85rem" }}>
          {error}
        </p>
      )}
      {/* TODO: Enhance FileUploadField to provide metadata callback for proper file submission */}
    </div>
  );
}

export default SharePage;

function FormSkeleton(): JSX.Element {
  return (
    <>
      <div className="surface" style={{ padding: "0", marginBottom: "1rem", borderRadius: "8px", overflow: "hidden" }}>
        <div style={{ height: "10px", background: "var(--color-border)", opacity: 0.5 }} />
        <div style={{ padding: "1.5rem 2rem" }}>
          <div style={{ height: "2rem", width: "60%", background: "var(--color-border)", marginBottom: "1rem", borderRadius: "4px", opacity: 0.5 }} className="skeleton-pulse" />
          <div style={{ height: "1rem", width: "80%", background: "var(--color-border)", borderRadius: "4px", opacity: 0.3 }} className="skeleton-pulse" />
        </div>
      </div>
      <div className="surface" style={{ padding: "2rem", display: "grid", gap: "1.5rem" }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ display: "grid", gap: "0.5rem" }}>
            <div style={{ height: "1rem", width: "30%", background: "var(--color-border)", borderRadius: "4px", opacity: 0.5 }} className="skeleton-pulse" />
            <div style={{ height: "2.5rem", width: "100%", background: "var(--color-border)", borderRadius: "10px", opacity: 0.3 }} className="skeleton-pulse" />
          </div>
        ))}
      </div>
      <style>{`
        @keyframes pulse {
          0% { opacity: 0.3; }
          50% { opacity: 0.6; }
          100% { opacity: 0.3; }
        }
        .skeleton-pulse {
          animation: pulse 1.5s infinite ease-in-out;
        }
      `}</style>
    </>
  );
}
